import React, { useEffect, useState, useMemo, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const LazyPublicTeamChart = lazy(() => import('./PublicTeamChart'));

const CHART_COLORS = [
  'var(--brand-primary)', 'var(--brand-secondary)', '#ff9a1f', '#c084fc', '#f472b6',
  '#34d399', '#fb923c', '#60a5fa', '#fbbf24', '#a78bfa',
];
const MONO    = "'JetBrains Mono', monospace";
const DISPLAY = "var(--font-display)";

function getISOWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}
function fmtDate(d) {
  return new Date(d).toLocaleDateString('de-DE', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  }).toUpperCase();
}
function fmtDateShort(d) {
  return new Date(d).toLocaleDateString('de-DE', {
    day: '2-digit', month: 'short',
  }).toUpperCase();
}
function toInitials(name) {
  return (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconHouse = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
    <polyline points="9 21 9 13 15 13 15 21"/>
  </svg>
);
const IconRider = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="17" cy="18.5" r="2.5"/>
    <circle cx="7" cy="18.5" r="2.5"/>
    <path d="M7 18.5h10"/>
    <path d="M12 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" fill="currentColor" stroke="none"/>
    <path d="M12 3l-2 5h5l3 4.5"/>
    <path d="M10 8l-3 4 4 2"/>
  </svg>
);
const IconCalendar = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PublicTeamPage() {
  const { slug }              = useParams();
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('dashboard');
  const [eventsTab, setEventsTab] = useState('upcoming');

  useEffect(() => {
    const existingToken = localStorage.getItem('token');
    if (!existingToken) {
      axios.get(`/api/public/${slug}/token`)
        .then(res => {
          localStorage.setItem('token', res.data.token);
          localStorage.setItem('userInfo', JSON.stringify(res.data.user));
          window.location.href = `/t/${slug}`;
        })
        .catch(() => {
          axios.get(`/api/public/${slug}`)
            .then(r => setData(r.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
        });
    } else {
      axios.get(`/api/public/${slug}`)
        .then(res => setData(res.data))
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }
  }, [slug]);

  // ── OG meta tags ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!data?.team?.name) return;
    const teamName = data.team.name;
    const ogImg = `${window.location.origin}/api/public/${slug}/og.png`;
    function setMeta(property, content) {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
      el.setAttribute('content', content);
    }
    function setNameMeta(name, content) {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    }
    document.title = `${teamName} · Crit Roster`;
    setMeta('og:title', `${teamName} · Crit Roster`);
    setMeta('og:description', `Events, Fahrer & Punktestand von ${teamName} auf Crit Roster.`);
    setMeta('og:image', ogImg);
    setMeta('og:image:width', '1200');
    setMeta('og:image:height', '630');
    setMeta('og:type', 'website');
    setMeta('og:url', window.location.href);
    setNameMeta('twitter:card', 'summary_large_image');
    setNameMeta('twitter:image', ogImg);
    setNameMeta('twitter:title', `${teamName} · Crit Roster`);
    setNameMeta('twitter:description', `Events, Fahrer & Punktestand von ${teamName}.`);
    return () => { document.title = 'Crit Roster'; };
  }, [data, slug]);

  // ── Season chart data ─────────────────────────────────────────────────────
  const { chartData, chartDrivers, totalPts } = useMemo(() => {
    if (!data?.season?.length || !data?.drivers?.length)
      return { chartData: [], chartDrivers: [], totalPts: 0 };

    const byDriver = {};
    for (const row of data.season) {
      if (!byDriver[row.driver_id]) byDriver[row.driver_id] = {};
      byDriver[row.driver_id][row.week] = row.points;
    }
    const driverIds   = Object.keys(byDriver);
    const currentWeek = getISOWeek(new Date());
    const allWeeks    = Array.from({ length: currentWeek }, (_, i) => i + 1);

    const chartData = allWeeks.map(week => {
      const row = { week };
      for (const did of driverIds) {
        const ws = Object.keys(byDriver[did]).map(Number).filter(w => w <= week);
        row[did] = ws.length ? byDriver[did][Math.max(...ws)] : 0;
      }
      return row;
    }).filter(row => driverIds.some(did => (row[did] || 0) > 0));

    const lastRow      = chartData[chartData.length - 1] || {};
    const chartDrivers = driverIds
      .map(did => ({ id: did, pts: lastRow[did] || 0 }))
      .sort((a, b) => b.pts - a.pts)
      .map(({ id }) => data.drivers.find(d => String(d.id) === id))
      .filter(Boolean);
    const totalPts = chartDrivers.reduce((s, d) => s + (lastRow[String(d.id)] || 0), 0);

    return { chartData, chartDrivers, totalPts };
  }, [data]);

  // ── Split events ──────────────────────────────────────────────────────────
  const { upcoming, past } = useMemo(() => {
    if (!data?.events) return { upcoming: [], past: [] };
    const today = new Date().toISOString().slice(0, 10);
    return {
      upcoming: data.events.filter(e => e.date >= today),
      past:     [...data.events.filter(e => e.date < today)].reverse(),
    };
  }, [data]);

  // ── Drivers sorted by points desc ─────────────────────────────────────────
  const sortedDrivers = useMemo(() => {
    if (!data?.drivers?.length) return [];
    const lastRow = chartData[chartData.length - 1] || {};
    return [...data.drivers].sort((a, b) => (lastRow[String(b.id)] || 0) - (lastRow[String(a.id)] || 0));
  }, [data, chartData]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--background, #111318)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: MONO, fontSize: 11, color: '#849495', letterSpacing: '0.1em' }}>LADEN...</div>
    </div>
  );
  if (error || !data) return (
    <div style={{ minHeight: '100vh', background: 'var(--background, #111318)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: MONO, fontSize: 11, color: '#ef4444' }}>Team nicht gefunden.</div>
    </div>
  );

  const teamName = data.team.name.toUpperCase();

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: <IconHouse size={26} /> },
    { key: 'drivers',   label: 'Fahrer',    icon: <IconRider size={26} /> },
    { key: 'events',    label: 'Events',    icon: <IconCalendar size={26} /> },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background, #111318)' }}>
      <style>{`
        @media (min-width: 768px) { .pub-cta-desktop { display: block !important; } }
      `}</style>

      {/* ── FIXED DESKTOP CTA — top-right, visible on all tabs ─────────── */}
      <div className="pub-cta-desktop" style={{ display: 'none', position: 'fixed', top: 20, right: 24, zIndex: 200 }}>
        <a href="/register" style={{
          fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
          background: 'var(--primary, var(--brand-primary))', color: '#002022',
          padding: '10px 18px', textDecoration: 'none', display: 'block',
        }}>
          KOSTENLOS STARTEN →
        </a>
      </div>

      {/* ── DESKTOP SIDEBAR ─────────────────────────────────────────────── */}
      <aside className="app-sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>🏍️</span>
            {teamName}
          </div>
          <div style={{ marginTop: 4, fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--primary, var(--brand-primary))', opacity: 0.7 }}>
            powered by Crit Roster
          </div>
          <div style={{ marginTop: 8 }}>
            <span style={{ padding: '2px 6px', background: 'rgba(255,154,31,0.15)', color: '#ff9a1f', fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', borderRadius: 2 }}>
              DEMO
            </span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`sidebar-link${tab === key ? ' active' : ''}`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%' }}
            >
              <span className="sidebar-link-icon" style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
              {label}
            </button>
          ))}
        </nav>
      </aside>

      {/* ── MOBILE TOPBAR ───────────────────────────────────────────────── */}
      <header className="mobile-topbar">
        <div className="mobile-topbar-brand" style={{ flex: 1 }}>
          <span style={{ fontSize: 22 }}>🏍️</span>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span style={{ fontFamily: DISPLAY, fontSize: 15, fontWeight: 800, fontStyle: 'italic', letterSpacing: '0.04em', color: 'var(--on-surface)' }}>{teamName}</span>
            <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--primary, var(--brand-primary))', marginTop: 2 }}>DEMO · powered by Crit Roster</span>
          </div>
        </div>
        <a href="/register" style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', background: 'var(--primary, var(--brand-primary))', color: '#002022', padding: '6px 10px', textDecoration: 'none', flexShrink: 0, whiteSpace: 'nowrap' }}>
          STARTEN →
        </a>
      </header>

      {/* ── MOBILE BOTTOM NAV ───────────────────────────────────────────── */}
      <nav className="mobile-bottom-nav">
        {navItems.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`mobile-nav-item${tab === key ? ' active' : ''}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', flex: 1 }}
            title={label}
          >
            <span className="mobile-nav-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
          </button>
        ))}
      </nav>

      {/* ── MAIN CONTENT ────────────────────────────────────────────────── */}
      <main className="app-main">
        <div className="app-content">

          {/* ──────────── DASHBOARD TAB ──────────── */}
          {tab === 'dashboard' && (
            <div>
              {/* Big team name + marketing tagline */}
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--primary, var(--brand-primary))', letterSpacing: '0.18em', marginBottom: 8 }}>
                  SAISON {new Date().getFullYear()}
                </div>
                <h1 style={{
                  fontFamily: DISPLAY, fontStyle: 'italic', fontWeight: 900,
                  fontSize: 'clamp(28px, 5vw, 52px)', letterSpacing: '-0.01em',
                  textTransform: 'uppercase', margin: '0 0 14px 0',
                  color: 'var(--on-surface, #e2e2e8)', lineHeight: 1,
                }}>
                  {teamName}
                </h1>
                <div style={{ fontFamily: MONO, fontSize: 10, color: '#849495', letterSpacing: '0.04em', fontStyle: 'italic', lineHeight: 1.6 }}>
                  "We use Crit Roster to organize our cycling team —<br />
                  so our riders always know where to race next."
                </div>
              </div>

              {chartData.length > 0 ? (
                <Suspense fallback={<div style={{ height: 310, background: 'var(--surface)', border: '1px solid var(--outline-variant)' }} />}>
                  <LazyPublicTeamChart
                    chartData={chartData}
                    chartDrivers={chartDrivers}
                    totalPts={totalPts}
                    drivers={data?.drivers || []}
                  />
                </Suspense>
              ) : (
                <div style={{ padding: '32px', background: 'var(--surface)', border: '1px solid var(--outline-variant)', textAlign: 'center', fontFamily: MONO, fontSize: 10, color: '#849495', letterSpacing: '0.1em' }}>
                  NOCH KEINE PUNKTEDATEN
                </div>
              )}
            </div>
          )}

          {/* ──────────── FAHRER TAB ──────────── */}
          {tab === 'drivers' && (
            <div>
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--primary, var(--brand-primary))', letterSpacing: '0.18em', marginBottom: 4 }}>
                  KADER · {sortedDrivers.length} FAHRER
                </div>
                <h1 style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontWeight: 900, fontSize: 28, letterSpacing: '0.02em', textTransform: 'uppercase', margin: 0, color: 'var(--on-surface, #e2e2e8)' }}>
                  Fahrer
                </h1>
              </div>

              {/* Grid — sorted by pts desc, initials only */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                {sortedDrivers.map((d, rankIdx) => {
                  const color   = CHART_COLORS[rankIdx % CHART_COLORS.length];
                  const initials = toInitials(d.name);
                  const lastRow  = chartData[chartData.length - 1] || {};
                  const pts      = lastRow[String(d.id)] || 0;
                  return (
                    <div key={d.id} style={{ background: 'var(--surface)', border: '1px solid var(--outline-variant)', padding: '16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <div style={{ width: 48, height: 48, borderRadius: '50%', border: `2px solid ${color}`, background: 'rgba(40,42,46,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                          {d.photo_url
                            ? <img src={d.photo_url} alt={initials} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(4px) brightness(0.7)' }} />
                            : <span style={{ fontFamily: DISPLAY, fontSize: 15, fontWeight: 800, color }}>{initials}</span>
                          }
                        </div>
                        {/* Rank badge for top 3 */}
                        {rankIdx < 3 && (
                          <div style={{
                            position: 'absolute', bottom: -2, right: -2,
                            width: 17, height: 17, borderRadius: '50%',
                            background: rankIdx === 0 ? '#FFD700' : rankIdx === 1 ? '#C0C0C0' : '#CD7F32',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontFamily: MONO, fontSize: 8, fontWeight: 700, color: '#000',
                          }}>
                            {rankIdx + 1}
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontWeight: 800, fontSize: 20, color, letterSpacing: '0.04em', lineHeight: 1 }}>{initials}</div>
                        <div style={{ fontFamily: MONO, fontSize: 9, color: '#849495', letterSpacing: '0.08em', marginTop: 3 }}>{d.category}</div>
                        {pts > 0 && (
                          <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color, marginTop: 5 }}>
                            {pts} <span style={{ fontSize: 8, opacity: 0.6 }}>Pkt</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ──────────── EVENTS TAB ──────────── */}
          {tab === 'events' && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--primary, var(--brand-primary))', letterSpacing: '0.18em', marginBottom: 4 }}>KALENDER</div>
                <h1 style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontWeight: 900, fontSize: 28, letterSpacing: '0.02em', textTransform: 'uppercase', margin: 0, color: 'var(--on-surface, #e2e2e8)' }}>
                  Events
                </h1>
              </div>

              {/* Upcoming / Past sub-tabs */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid rgba(59,73,75,0.3)' }}>
                {[
                  ['upcoming', `UPCOMING (${upcoming.length})`],
                  ['past',     `VERGANGEN (${past.length})`],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setEventsTab(key)}
                    style={{
                      padding: '9px 18px', fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
                      background: 'transparent', border: 'none',
                      borderBottom: eventsTab === key ? '2px solid var(--primary, var(--brand-primary))' : '2px solid transparent',
                      color: eventsTab === key ? 'var(--primary, var(--brand-primary))' : 'rgba(185,202,203,0.5)',
                      cursor: 'pointer', transition: 'all 0.15s', marginBottom: -1,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Upcoming list */}
              {eventsTab === 'upcoming' && (
                upcoming.length === 0 ? (
                  <div style={{ padding: '32px', textAlign: 'center', fontFamily: MONO, fontSize: 10, color: '#849495', letterSpacing: '0.1em' }}>
                    KEINE ANSTEHENDEN EVENTS
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {upcoming.map(ev => {
                      const regs = (data.regsByEvent || {})[ev.id] || [];
                      return (
                        <div key={ev.id} style={{ background: 'var(--surface)', border: '1px solid var(--outline-variant)', padding: '16px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontWeight: 800, fontSize: 15, color: 'var(--on-surface)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                {ev.name}
                              </div>
                              <div style={{ fontFamily: MONO, fontSize: 9, color: '#849495', marginTop: 4 }}>
                                {fmtDate(ev.date)}{ev.location ? ` · ${ev.location}` : ''}
                              </div>
                            </div>
                            <span style={{ padding: '3px 8px', background: 'rgba(var(--brand-secondary-rgb),0.12)', border: '1px solid rgba(var(--brand-secondary-rgb),0.3)', fontFamily: MONO, fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--brand-secondary)', flexShrink: 0 }}>
                              UPCOMING
                            </span>
                          </div>
                          {/* Startliste — initials only, no names */}
                          {regs.length > 0 && (
                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(59,73,75,0.3)' }}>
                              <div style={{ fontFamily: MONO, fontSize: 8, color: 'rgba(var(--brand-primary-rgb),0.5)', letterSpacing: '0.12em', marginBottom: 8 }}>
                                STARTLISTE · {regs.length} FAHRER
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {regs.map(r => {
                                  const dIdx  = data.drivers.findIndex(d => d.id === r.driver_id);
                                  const color = CHART_COLORS[dIdx % CHART_COLORS.length] || '#849495';
                                  const inits = toInitials(r.driver_name);
                                  return (
                                    <div
                                      key={r.driver_id}
                                      title={inits}
                                      style={{ width: 28, height: 28, borderRadius: '50%', border: `1.5px solid ${color}`, background: 'rgba(40,42,46,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}
                                    >
                                      {r.photo_url
                                        ? <img src={r.photo_url} alt={inits} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(3px) brightness(0.7)' }} loading="lazy" />
                                        : <span style={{ fontFamily: DISPLAY, fontSize: 9, fontWeight: 800, color }}>{inits}</span>
                                      }
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* Past list */}
              {eventsTab === 'past' && (
                past.length === 0 ? (
                  <div style={{ padding: '32px', textAlign: 'center', fontFamily: MONO, fontSize: 10, color: '#849495', letterSpacing: '0.1em' }}>
                    KEINE VERGANGENEN EVENTS
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {past.map(ev => (
                      <div key={ev.id} style={{ background: 'var(--surface)', border: '1px solid var(--outline-variant)', padding: '14px 20px', opacity: 0.8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                          <div>
                            <div style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontWeight: 800, fontSize: 14, color: 'var(--on-surface)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                              {ev.name}
                            </div>
                            <div style={{ fontFamily: MONO, fontSize: 9, color: '#849495', marginTop: 3 }}>
                              {fmtDateShort(ev.date)}{ev.location ? ` · ${ev.location}` : ''}
                            </div>
                          </div>
                          <span style={{ padding: '3px 8px', background: 'rgba(59,73,75,0.2)', fontFamily: MONO, fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', color: '#849495', flexShrink: 0 }}>
                            ABGESCHLOSSEN
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
